<script setup>
import { computed } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeTab: {
    type: String,
    default: wootConstants.ASSIGNEE_TYPE.ME,
  },
});

const emit = defineEmits(['chatTabChange']);

// Teletel (owner directive): every view is a tab, all of them on one row. The
// bar used to keep three and hide the rest behind a "⋮" menu to stop them
// overlapping; the tabs are compact and share the row evenly instead, so a view
// is never a click away behind a menu. Each tab is flex-1 with a truncating
// label, which is what guarantees they cannot overlap however narrow the list
// column gets.
const activeTabIndex = computed(() => {
  return props.items.findIndex(item => item.key === props.activeTab);
});

const selectKey = key => {
  if (key !== props.activeTab) {
    emit('chatTabChange', key);
  }
};

const onTabChange = selectedTabIndex => {
  if (selectedTabIndex >= 0 && selectedTabIndex < props.items.length) {
    selectKey(props.items[selectedTabIndex].key);
  }
};

const keyboardEvents = {
  'Alt+KeyN': {
    action: () => {
      const nextIndex = (activeTabIndex.value + 1) % props.items.length;
      onTabChange(nextIndex);
    },
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div class="flex items-center w-full px-3 -mt-1 h-10 gap-0.5">
    <button
      v-for="item in items"
      :key="item.key"
      type="button"
      :title="`${item.name} (${item.count})`"
      :aria-current="item.key === activeTab ? 'page' : undefined"
      class="relative flex h-10 min-w-0 flex-auto items-center justify-center gap-1 px-1 text-xs font-medium after:absolute after:bottom-px after:left-0 after:right-0 after:h-[2px] after:rounded-full after:transition-all after:duration-200"
      :class="
        item.key === activeTab
          ? 'text-n-blue-11 after:bg-n-brand after:opacity-100'
          : 'text-n-slate-11 after:bg-transparent after:opacity-0 hover:text-n-slate-12'
      "
      @click="selectKey(item.key)"
    >
      <span class="truncate">{{ item.name }}</span>
      <span
        v-if="item.count"
        class="flex h-4 min-w-4 flex-shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-semibold"
        :class="
          item.key === activeTab
            ? 'bg-n-blue-3 text-n-blue-11'
            : 'bg-n-alpha-1 text-n-slate-10'
        "
      >
        {{ item.count }}
      </span>
    </button>
  </div>
</template>
